import React, { useContext, useState } from 'react';
import { Link, Outlet } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './sidebar.scss';
import { getItem, setItem } from '../../../services/session-storage.service';
import { GlobalAppContext } from '../../../context/GlobalContext';
import Redirect from '../../redirect/redirect';
import { FiBookmark, FiCalendar, FiChevronLeft, FiChevronRight, FiHome, FiBarChart2, FiSettings } from 'react-icons/fi';
import { BiLogOut } from 'react-icons/bi';
import { TfiClose, TfiMenu } from 'react-icons/tfi';
import { BiLogIn } from 'react-icons/bi';

function BackOfficeSideBar() {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(true);
  const [activeItem, setActiveItem] = useState('dashboard');
  const [isMobile] = useState(false);
  const { user, setToken, setUser, setIsUserSignedIn } = useContext(GlobalAppContext);

  const token = getItem('token');

  const toggleNavigation = () => {
    setIsExpanded(!isExpanded);
  };

  const handleItemClick = itemId => {
    setActiveItem(itemId);
    // Close mobile menu when item is selected
    if (isMobile) {
      setIsExpanded(false);
    }
    if (itemId === 'signout') {
      handleSignOut();
    }
  };

  const handleSignOut = () => {
    // TODO - Add API call to cancel token first
    setIsUserSignedIn(false);
    setToken('');
    setUser({ id: 0, countryId: 0, roleId: 0, name: '', email: '', dateOfBirth: '', isActive: false, isBackOffice: false });
    setItem('token', '');
    window.location.href = '/';
  };

  const clickHandler = () => {
    return;
  };

  const navigationItems = [
    { id: 'home', icon: FiHome, label: 'Home', path: '/back-office', onClick: { clickHandler }, color: 'text-blue-600' },
    {
      id: 'interests',
      icon: FiBookmark,
      label: t('SIDEBAR.INTERESTS'),
      path: 'interests',
      onClick: { clickHandler },
      color: 'text-purple-600'
    },
    {
      id: 'newsletters',
      icon: FiCalendar,
      label: t('SIDEBAR.NEWSLETTERS'),
      path: 'newsletters',
      onClick: { clickHandler },
      color: 'text-purple-600'
    },
    { id: 'reports', icon: FiBarChart2, label: t('SIDEBAR.REPORTS'), path: 'reports', onClick: { clickHandler }, color: 'text-pink-600' },
    {
      id: 'settings',
      icon: FiSettings,
      label: t('SIDEBAR.SETTINGS'),
      path: 'settings',
      onClick: { clickHandler },
      color: 'text-green-600'
    },
    { id: 'signout', icon: BiLogOut, label: t('SIDEBAR.SIGN_OUT'), path: '/signout', onClick: { handleSignOut }, color: 'text-yellow-600' }
  ];

  return (
    <>
      {!token && <Redirect path='/back-office/signin' />}
      <div className='flex h-screen bg-gray-50 relative'>
        {/* Mobile Overlay */}
        {isMobile && isExpanded && <div className='fixed inset-0 bg-black bg-opacity-50 z-10' onClick={() => setIsExpanded(false)} />}

        {/* Mobile Header */}
        {isMobile && (
          <div className='fixed top-0 left-0 right-0 bg-white shadow-sm border-b border-gray-200 z-20 h-16'>
            <div className='flex items-center justify-between px-4 h-full'>
              <button onClick={toggleNavigation} className='p-2 rounded-lg hover:bg-gray-100 transition-colors duration-200'>
                {isExpanded ? <TfiClose className='w-6 h-6 text-gray-600' /> : <TfiMenu className='w-6 h-6 text-gray-600' />}
              </button>
              <h1 className='text-base font-bold text-gray-800'>AthleteHub</h1>
              <div className='w-10 h-8 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center'>
                <span className='text-white text-xs font-semibold'>JD</span>
              </div>
            </div>
          </div>
        )}

        {/* Navigation Sidebar */}
        <nav
          className={`
          ${
            isMobile
              ? `fixed top-0 left-0 h-full w-64 z-20 transform transition-transform duration-300 ${
                  isExpanded ? 'translate-x-0' : '-translate-x-full'
                }`
              : `${isExpanded ? 'w-64' : 'w-16'} transition-all duration-300 ease-in-out`
          }
          bg-white shadow-xl border-r border-gray-200 
          flex flex-col
          ${isMobile ? 'pt-16' : ''}
        `}>
          {/* Header - Desktop Only */}
          {!isMobile && (
            <div className='flex items-center justify-between border-b border-gray-200'>
              <div className={`${isExpanded ? 'block' : 'hidden'} transition-all duration-300`}>
                <h1 className='sidebar-logo text-xs font-light'>{t('APP_NAME')}</h1>
                <p className='text-sm text-gray-500 text-center'>Back Office</p>
              </div>
              <button onClick={toggleNavigation} className='p-2 rounded-lg hover:bg-gray-100 transition-colors duration-200'>
                {isExpanded ? <FiChevronLeft className='w-5 h-5 text-gray-600' /> : <FiChevronRight className='w-5 h-5 text-gray-600' />}
              </button>
            </div>
          )}

          {/* Navigation Items */}
          <div className='min-w-1/1 flex-1 py-4'>
            <ul className='space-y-1 px-3'>
              {token
                ? navigationItems.map(item => {
                    const Icon = item.icon;
                    const isActive = activeItem === item.id;

                    return (
                      <li key={item.id}>
                        <Link to={item.path}>
                          <button
                            onClick={() => handleItemClick(item.id)}
                            className={`
                      w-full flex items-center px-3 py-3 left-menu-button
                      transition-all duration-200 group
                      ${
                        isActive
                          ? 'bg-blue-50 border-r-4 border-blue-600 text-blue-700'
                          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      }
                    `}
                            title={!isExpanded && !isMobile ? item.label : undefined}>
                            <Icon
                              className={`
                        w-5 h-5 flex-shrink-0
                        ${isActive ? item.color : 'text-gray-500 group-hover:text-gray-700'}
                        transition-colors duration-200
                      `}
                            />
                            <span
                              className={`
                        ml-3 text-sm ml-5
                        ${isExpanded || isMobile ? 'block' : 'hidden'}
                        transition-all duration-300
                      `}>
                              {item.label}
                            </span>
                          </button>
                        </Link>
                      </li>
                    );
                  })
                : null}
              {!token ? (
                <li key='signin'>
                  <button
                    onClick={() => handleItemClick('signin')}
                    className={`
                      w-full flex items-center px-3 py-3 left-menu-button
                      transition-all duration-200 group
                      ${
                        activeItem
                          ? 'bg-blue-50 border-r-4 border-blue-600 text-blue-700'
                          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      }
                    `}
                    title={!isExpanded ? 'Sign In' : undefined}>
                    <BiLogIn
                      className={`
                        w-5 h-5 flex-shrink-0
                        ${activeItem ? 'text-amber-600' : 'text-gray-500 group-hover:text-gray-700'}
                        transition-colors duration-200
                      `}
                    />
                    <span
                      className={`
                        ml-3 text-sm ml-5
                        ${isExpanded || isMobile ? 'block' : 'hidden'}
                        transition-all duration-300
                      `}>
                      {t('SIDEBAR.SIGN_IN')}
                    </span>
                  </button>
                </li>
              ) : null}
            </ul>
          </div>

          {/* User Profile Section */}
          {token ? (
            <div className='border-t border-gray-200 p-4'>
              <div className='flex items-center'>
                <div className='w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center'>
                  <span className='text-white text-sm font-semibold'>JD</span>
                </div>
                <div className={`${isExpanded || isMobile ? 'block ml-3' : 'hidden'} transition-all duration-300`}>
                  <p className='text-sm font-medium text-gray-800'>{user.name}</p>
                  <p className='text-xs text-gray-500'>Professional Athlete</p>
                </div>
              </div>
            </div>
          ) : null}
        </nav>

        {/* Main Content Area */}
        <main className={`flex-1 p-8 ${isMobile ? 'pt-24' : ''}`}>
          <Outlet />
        </main>
      </div>
    </>
  );
}

export default BackOfficeSideBar;
